import { usePlatewell, useStore } from '@platewell/query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useShowToast } from '../components/toast.js';
import { quotaRefusal, UpgradeNotice, type QuotaRefusal } from '../components/upgrade-notice.js';
import { saveSharedRecipes, shareSavedMessage } from '../lib/share-import.js';
import { importRoute } from '../router.js';

/**
 * A shared link saves itself.
 *
 * The recipes are copied into the household's import collection and the screen hands over
 * to that collection, so the link costs one tap rather than a form — the sender already
 * decided what is in it, and there is nothing here the recipient cannot change afterwards.
 * New ids are minted, history starts fresh, and nothing stays linked to the sender: their
 * later edits, or their deleting the share, cannot touch the copy.
 */
export function ImportShare() {
  const { token } = importRoute.useParams();
  const { activeHouseholdId } = usePlatewell();
  const store = useStore();
  const navigate = useNavigate();
  const showToast = useShowToast();
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);
  // The save runs once per link, not once per render — and not twice under StrictMode,
  // which would leave the household holding two copies.
  const started = useRef<string | null>(null);

  useEffect(() => {
    if (activeHouseholdId == null || started.current === token) return;
    started.current = token;

    void (async () => {
      try {
        const result = await saveSharedRecipes(store, activeHouseholdId, token);
        // Nothing fitted, so there is nowhere to send them: the paywall is the whole answer.
        const blocked = quotaRefusal(result.blockedBy);
        const [first, ...rest] = result.recipeIds;
        if (blocked != null && first == null) {
          setRefusal(blocked);
          return;
        }
        // One recipe is the thing itself, so open it; a collection of them is a list, and
        // the collection page is that list. `replace` either way, so Back leaves the flow
        // rather than re-running the save.
        await (first != null && rest.length === 0
          ? navigate({
              to: '/recipes/$recipeId',
              params: { recipeId: first },
              // Carried so the recipe knows which list it belongs to, exactly as it would
              // had the user opened it from the collection themselves.
              search: { collectionId: result.collectionId },
              replace: true,
            })
          : navigate({
              to: '/collections/$collectionId',
              params: { collectionId: result.collectionId },
              replace: true,
            }));
        showToast(shareSavedMessage(result));
      } catch (cause) {
        const quota = quotaRefusal(cause);
        if (quota != null) setRefusal(quota);
        else setError(cause instanceof Error ? cause.message : 'Could not save this link');
      }
    })();
  }, [activeHouseholdId, navigate, showToast, store, token]);

  if (refusal != null) {
    return (
      <div className="empty">
        <h2>No room for these recipes</h2>
        <UpgradeNotice refusal={refusal} action="save them" />
      </div>
    );
  }

  if (error != null) {
    return (
      <div className="empty">
        <h2>Could not save this link</h2>
        <p className="hint">{error}</p>
      </div>
    );
  }

  return (
    <div className="empty">
      <p className="hint">Saving these recipes…</p>
    </div>
  );
}
